<template>
  <div class="slider-container-wrapper d-flex align-center">
    <div class="slider-container" ref="slider" @click="barClick">
      <!-- Measurement lines and labels -->
      <div
        v-for="(point, index) in measurementPoints"
        :key="index"
        class="measurement-line"
        :style="{ left: `${point}%` }"
      >
        <div class="measurement-label">{{ labels[index] }}</div>
      </div>
      <div class="slider-track d-flex justify-content-center">
        <span class="slider-help-text">Zoom och Panorera</span>
      </div>
      <div class="slider-outside-range left" :style="{ width: `${minHandlePercent}%` }"></div>
      <div class="slider-outside-range right" :style="{ width: `${100 - maxHandlePercent}%` }"></div>
      <div
        class="slider-range"
        :style="{
          left: `${minHandlePercent}%`,
          width: `calc(${maxHandlePercent - minHandlePercent}% + 1px)`,
        }"
        @mousedown.prevent="handleMouseDown('range', $event)"
      ></div>
      <span class="mdi mdi-tally-mark-2"></span>
      <div
        class="slider-handle left-handle d-flex align-center"
        :style="{ left: `${minHandlePercent}%` }"
        @mousedown.prevent="handleMouseDown('min', $event)"
      >
        <div class="slider-handle-graphic align-self-center"></div>
      </div>
      <div
        class="slider-handle right-handle d-flex align-center"
        :style="{ left: `${maxHandlePercent}%` }"
        @mousedown.prevent="handleMouseDown('max', $event)"
      >
        <div class="slider-handle-graphic align-self-center"></div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  props: {
    labels: {
      type: Array,
      default: () => [],
    },
    externalMin: {
      type: Number,
      default: 0,
    },
    externalMax: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      sliderWidth: 0,
      dragging: null,
      minHandlePercent: this.externalMin,
      maxHandlePercent: this.externalMax,
      minGap: 52, // The minimum distance between handles, in pixels
      dragStartPercent: 0,
      handleStartPercent: 0,
      rangeStartMinPercent: 0,
      rangeStartMaxPercent: 100,
      measurementPoints: this.calculateMeasurementPoints(this.labels),
      hasSnapped: false,
      snapThreshold: 1.75,
      breakFreeThreshold: 2.5,
      blockMovement: false,
      isRangePanning: false,
      hasDragged: false,
    };
  },
  created() {
    this.emitRangeChange(); // Emit initial range
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.drag);
    window.removeEventListener("mouseup", this.stopDrag);
  },
  watch: {
    labels(newLabels) {
      this.measurementPoints = this.calculateMeasurementPoints(newLabels);
    },
    externalMin(newMin) {
      if (newMin !== this.minHandlePercent) {
        this.minHandlePercent = newMin;
      }
    },
    externalMax(newMax) {
      if (newMax !== this.maxHandlePercent) {
        this.maxHandlePercent = newMax;
      }
    },
  },
  computed: {
    minGapPercent() {
      return (this.minGap / this.sliderWidth) * 100;
    },
  },
  methods: {
    calculateMeasurementPoints(labels) {
      if (labels.length <= 1) {
        return [0, 100]; // Default to start and end if one or no labels provided
      }
      const interval = 100 / labels.length;
      return labels.map((label, index) => interval * index);
    },
    handleMouseDown(handle, event) {
      event.stopPropagation();
      this.hasDragged = false;
      this.initDrag(handle, event);
    },
    initDrag(handle, event) {
      event.preventDefault();
      this.dragging = handle;
      this.isRangePanning = handle === "range";
      const rect = this.$refs.slider.getBoundingClientRect();
      this.sliderWidth = rect.width;

      this.dragStartPercent =
        ((event.clientX - rect.left) / this.sliderWidth) * 100;

      if (handle === "min" || handle === "max") {
        this.handleStartPercent = this[handle + "HandlePercent"];
      } else if (handle === "range") {
        this.rangeStartMinPercent = this.minHandlePercent;
        this.rangeStartMaxPercent = this.maxHandlePercent;
      }

      window.addEventListener("mousemove", this.drag);
      window.addEventListener("mouseup", this.stopDrag);
    },

    drag(event) {
      if (!this.dragging) return;

      const rect = this.$refs.slider.getBoundingClientRect();
      let newPercent = ((event.clientX - rect.left) / this.sliderWidth) * 100;
      newPercent = this.clampPercentWithinBounds(newPercent);

      if (this.dragging === "min" || this.dragging === "max") {
        this.isRangePanning = false;
        let tempHandlePercent =
          this.dragging === "min"
            ? Math.min(newPercent, this.maxHandlePercent - this.minGapPercent)
            : Math.max(newPercent, this.minHandlePercent + this.minGapPercent);

        this[`${this.dragging}HandlePercent`] = this.applySnapping(
          tempHandlePercent
        );
      } else if (this.dragging === "range") {
        this.isRangePanning = true;
        let deltaPercent = newPercent - this.dragStartPercent;
        let newMinPercent = this.rangeStartMinPercent + deltaPercent;
        let newMaxPercent = this.rangeStartMaxPercent + deltaPercent;
        [newMinPercent, newMaxPercent] = this.clampRangePosition(
          newMinPercent,
          newMaxPercent
        );
        this.minHandlePercent = newMinPercent;
        this.maxHandlePercent = newMaxPercent;
      }

      if (
        !this.hasDragged &&
        Math.abs(newPercent - this.dragStartPercent) > 0.1
      ) {
        this.hasDragged = true;
      }

      if (this.hasDragged) {
        this.emitRangeChange()
      }
    },

    applySnapping(percent) {
      const closestSnapPoint = this.findClosestSnapPoint(percent);
      const distanceToSnapPoint = Math.abs(percent - closestSnapPoint);
      if (distanceToSnapPoint <= this.snapThreshold) {
        return closestSnapPoint;
      }
      return percent;
    },

    clampPercentWithinBounds(percent) {
      return Math.max(0, Math.min(percent, 100));
    },

    clampRangePosition(minPercent, maxPercent) {
      if (minPercent < 0) {
        maxPercent -= minPercent;
        minPercent = 0;
      } else if (maxPercent > 100) {
        minPercent -= maxPercent - 100;
        maxPercent = 100;
      }
      return [minPercent, maxPercent];
    },

    findClosestSnapPoint(percent) {
      let closestSnapPoint = this.measurementPoints[0];
      let closestDistance = Math.abs(percent - closestSnapPoint);

      this.measurementPoints.forEach((point) => {
        const distanceToPoint = Math.abs(percent - point);
        if (distanceToPoint < closestDistance) {
          closestSnapPoint = point;
          closestDistance = distanceToPoint;
        }
      });
      return closestSnapPoint;
    },

    stopDrag() {
      if (this.dragging) {
        window.removeEventListener("mousemove", this.drag);
        window.removeEventListener("mouseup", this.stopDrag);
        if (this.dragging === "range") {
          // If it was a range drag, ensure that the range does not snap to the mouseup position
          // Reset isRangePanning here to prevent it from affecting the next barClick
          this.isRangePanning = false;
        } else if (this.isSnapped) {
          // Snap individual handles to the closest snap point without further checks
          const snapPoint = this.findClosestSnapPoint(
            this[this.dragging + "HandlePercent"]
          );
          this[this.dragging + "HandlePercent"] = snapPoint;
        }

        if (this.hasDragged) {
          // Emit the on-range-change event with the final min and max handle positions
          this.emitRangeChange()
        }
        // Reset states for the next drag operation
        setTimeout(() => {
          this.dragging = null;
          this.isSnapped = false;
          this.blockMovement = false;
          this.hasDragged = false;
        }, 10);
      }
    },

    barClick(event) {
      if (this.dragging) return;
      const rect = this.$refs.slider.getBoundingClientRect();
      const clickPercent =
        ((event.clientX - rect.left) / this.sliderWidth) * 100;

      // Determine which handle is closer to the click position
      const distanceToMinHandle = Math.abs(
        clickPercent - this.minHandlePercent
      );
      const distanceToMaxHandle = Math.abs(
        clickPercent - this.maxHandlePercent
      );

      let handleToMove =
        distanceToMinHandle < distanceToMaxHandle ? "min" : "max";

      // Move the closest handle to the click position
      this[`${handleToMove}HandlePercent`] = clickPercent;

      // Find the closest snap point to the click position
      const snapPoint = this.findClosestSnapPoint(clickPercent);
      const distanceToSnapPoint = Math.abs(clickPercent - snapPoint);

      // Snap the handle to the closest snap point if within snap threshold
      if (distanceToSnapPoint <= this.snapThreshold) {
        this[`${handleToMove}HandlePercent`] = snapPoint;
      }

      // Update the handle value if needed
      this[`${handleToMove}HandleValue`] =
        (this[`${handleToMove}HandlePercent`] / 100) * this.sliderWidth;

      this.emitRangeChange()
    },
    emitRangeChange() {
      this.$emit("on-range-change", {
        minHandlePercent: this.minHandlePercent,
        maxHandlePercent: this.maxHandlePercent,
      });
    },
  },
};
</script>

<style>
.slider-container-wrapper {
  position: relative;
  width: 100%;
  height: 6.5rem;
  padding-top: 2rem;
}
.slider-container {
  position: relative;
  width: 100%;
  height: 4rem;
}
.slider-track {
  position: absolute;
  background-color: #f1f1f100;
  height: 100%;
  width: 100%;
}
.slider-range {
  position: absolute;
  background-color: #cae6ffa1;
  border-right: 1px solid rgba(115, 161, 199, 0.466);
  border-left: 1px solid rgba(115, 161, 199, 0.466);
  height: 100%;
  cursor: grab;
  z-index: 1;
  transition: background-color 0.3s ease;
  
}
.slider-range:hover {
  background-color: #9ed2ff7e;
}
.slider-handle {
  position: absolute;
  width: 2rem;
  margin-left: -1rem;
  height: 100%;
  cursor: ew-resize;
  z-index: 2;
}

@keyframes lightUpAndFade {
  0% {
    background-color: #337AB7;
  }
  50% {
    background-color: #3f89c9;
  }
  100% {
    background-color: #337AB7;
  }
}

.slider-handle-graphic {
  position: relative;
  height: 2rem;
  width: 2rem;
  border: 2px solid #337AB7;
  background-color: #337AB7;
  border-radius: 50%;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.13);
}

.slider-handle-graphic::before,
.slider-handle-graphic::after {
  content: "";
  position: absolute;
  top: 25%;
  width: 2px;
  height: 50%;
  background-color: rgba(255, 255, 255, 0.781);
}

.slider-handle-graphic::before {
  left: 9px;
}

.slider-handle-graphic::after {
  right: 9px;
}

.slider-container:hover .slider-handle-graphic {
  animation: lightUpAndFade 0.5s;
}

.slider-container:hover .slider-handle-graphic:hover {
  background-color: rgb(67, 113, 173);
}
.left-handle::after,
.right-handle::after {
  content: "";
}

.slider-outside-range {
  position: absolute;
  background-color: #efefefd4;
  height: 100%;
  top: 0;
  z-index: 0;
}

.slider-outside-range.left {
  left: 0;
}

.slider-outside-range.right {
  right: 0;
}

.slider-help-text {
  position: relative;
  background-color: rgba(255, 255, 255, 0.205);
  padding: 1rem;
  color: gray;
  z-index: 0;
}

.slider-range:hover .slider-help-text {
  color: #0000007e;
}

.measurement-line {
  position: absolute;
  height: 100%;
  width: 1px;
  background-color: rgb(214, 214, 214);
  z-index: 1;
}

.measurement-label {
  position: absolute;
  top: -1.75em;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 0.75rem;
  user-select: none;
}
</style>
